/**
 * Company Item Service
 *
 * Handles API calls for Company Resources
 * Provides access to items assigned to the company by Super Admin
 */

import { apiClient } from '@api'

const companyItemService = {
  /**
   * Get all items assigned to the company
   * @param {Object} filters - Filter options
   * @param {Object} pagination - Pagination options
   * @returns {Promise} - Items list with pagination
   */
  async getAll(filters = {}, pagination = {}) {
    const params = new URLSearchParams()

    if (filters.search) params.append('search', filters.search)
    if (filters.type && filters.type !== 'all') params.append('type', filters.type)

    params.append('page', pagination.page || 1)
    params.append('limit', pagination.limit || 25)

    const queryString = params.toString()
    return apiClient.get(`/company-items${queryString ? `?${queryString}` : ''}`)
  },

  /**
   * Get single assigned item by ID
   * @param {number} id - Item ID
   * @returns {Promise} - Item details
   */
  async getById(id) {
    return apiClient.get(`/company-items/${id}`)
  },

  /**
   * Get statistics for company items
   * @returns {Promise} - Statistics data
   */
  async getStats() {
    return apiClient.get('/company-items/stats')
  },

  /**
   * Get download URL for an item
   * @param {number} id - Item ID
   * @returns {string} - Download URL
   */
  getDownloadUrl(id) {
    const baseUrl = apiClient.baseURL || import.meta.env.VITE_API_BASE_URL
    return `${baseUrl}/company-items/${id}/download`
  },

  /**
   * Download item file (logs the download)
   * @param {number} id - Item ID
   */
  async download(id) {
    const url = this.getDownloadUrl(id)
    const token = sessionStorage.getItem('auth_access_token') || localStorage.getItem('auth_access_token')

    // Create a temporary link with auth token
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      throw new Error('Download failed')
    }

    const blob = await response.blob()
    const contentDisposition = response.headers.get('content-disposition')
    let filename = 'download'

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }

    // Create download link
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  },

  /**
   * Update to latest version of an item
   * @param {number} id - Item ID
   * @returns {Promise}
   */
  async updateToLatestVersion(id) {
    return apiClient.post(`/company-items/${id}/update-version`)
  },

  /**
   * Get item types
   * @returns {Array} - Item type options
   */
  getItemTypes() {
    return [
      { value: 'policy', label: 'Policy', icon: 'FileText', color: '#3b82f6' },
      { value: 'procedure', label: 'Procedure', icon: 'List', color: '#10b981' },
      { value: 'template', label: 'Template', icon: 'FileTemplate', color: '#8b5cf6' },
      { value: 'guideline', label: 'Guideline', icon: 'BookOpen', color: '#f59e0b' },
      { value: 'resource', label: 'Resource', icon: 'Folder', color: '#6366f1' },
    ]
  },

  /**
   * Format file size
   * @param {number} bytes - File size in bytes
   * @returns {string} - Formatted file size
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  },

  /**
   * Get file icon based on mime type
   * @param {string} mimeType - File MIME type
   * @returns {string} - Icon name
   */
  getFileIcon(mimeType) {
    if (mimeType === 'application/pdf') return 'FileText'
    if (mimeType?.includes('word') || mimeType?.includes('document')) return 'FileType'
    return 'File'
  },

  /**
   * Get type label by value
   * @param {string} type - Type value
   * @returns {string} - Type label
   */
  getTypeLabel(type) {
    const types = this.getItemTypes()
    const found = types.find((t) => t.value === type)
    return found?.label || type
  },

  /**
   * Get type color by value
   * @param {string} type - Type value
   * @returns {string} - Type color
   */
  getTypeColor(type) {
    const types = this.getItemTypes()
    const found = types.find((t) => t.value === type)
    return found?.color || '#6b7280'
  },
}

export default companyItemService
